import { message } from "antd";
import { edit_invoice, emit_invoice } from "../../../services";
import { useRef, useState } from "react";

const getModifiedTaxAuthoritySpecifications = (
	invoice,
	company,
	sendWithIsr
) => {
	const updatedFrases = invoice.tax_authority_specifications.frases.map((frase) => ({
		...frase,
		scenario: isNaN(Number(frase.scenario)) ? frase.scenario : parseInt(frase.scenario, 10),
		type: isNaN(Number(frase.type)) ? frase.type : parseInt(frase.type, 10),
	}));		  

	const new_tax_authority_specifications = {
		frases: updatedFrases,
	};

	if(company.database !== undefined){
		if (!sendWithIsr) {
			new_tax_authority_specifications.frases =
				new_tax_authority_specifications.frases.filter(
					(frase) => !(frase.type === 1 && frase.scenario === 2)
				);
		}
	}

	return new_tax_authority_specifications;
};

const getInvoiceForSubmission = (invoice, company, sendWithIsr, position) => {
	return {
		id: invoice.id,
		items: invoice.items.map((item) => ({
			accounting_specifications: {
				[position]: {
					final_description: item.accounting_specifications[position].final_description,
					accounting_classification: `${item.accounting_specifications[position].accounting_classification}`,
					analytic_classification: `${item.accounting_specifications[position].analytic_classification}`,
				},
			},
		})),
		tax_authority_specifications: getModifiedTaxAuthoritySpecifications(
			invoice,
			company,
			sendWithIsr
		),
		accounting_specifications: {
			[position]: {
				description: invoice.accounting_specifications[position].description,
				diary: `${invoice.accounting_specifications[position].diary}`,
				[position === "in" ? "sale_type" : "expense_type"]: position === "in" 
					? invoice.accounting_specifications[position].sale_type 
					: invoice.accounting_specifications[position].expense_type,
				accounting_date: invoice.accounting_specifications[position].accounting_date,
				extras: invoice.accounting_specifications[position].extras,
				specs: invoice.accounting_specifications[position].specs,
			},
		},
	};
};

const areItemsComplete = (items, position) => {
	return items.every(
		(item) => item.accounting_specifications[position].accounting_classification !== ""
	);
};

const isInvoiceComplete = (invoice, position) => {
	const fields = ["diary", "description", position == "in"? "sale_type" : "expense_type"];
	return fields.every(
		(field) =>
			invoice.accounting_specifications[position][field] !== "" &&
			invoice.accounting_specifications[position][field] !== undefined
	);
};

export const useInvoiceSend = ({
	token,
	company,
	position,
	onSubmitInvoiceChanges,
	onResetInvoices,
	onChargeNext,
}) => {
	const [loadingInvoices, setLoadingInvoices] = useState([]); // IDs de facturas en proceso
	const processingQueue = useRef([]);

	// Procesa la siguiente factura en la cola
	const processNext = async () => {
		if (processingQueue.current.length === 0) return;
	
		const { method, args } = processingQueue.current.shift(); // Toma la siguiente tarea de la cola
		await method(...args); // Ejecuta el método correspondiente
		processNext(); // Procesa la siguiente tarea después de completar esta
	};

	// Agrega tareas a la cola
	const enqueueProcessing = (method, args) => {
		processingQueue.current.push({ method, args });
		if (processingQueue.current.length === 1) {
			processNext(); // Inicia la ejecución si es la primera tarea
		}
	};
	
	// Modifica el estado de la factura
	const handleInvoiceStateChange = async (editValue, invoice, state = null) => {
		const { id } = invoice;
		
		setLoadingInvoices((prev) => [...prev, id]);

		const invoicePayload = state === null ? { id } : { id, state };

		try {
			onChargeNext();
			const res = await edit_invoice(token, editValue, invoicePayload, position);
			if (res) {
				message.success(res.data);
				onSubmitInvoiceChanges(id);
			}
		} catch (error) {
			message.error(error.response?.data || "Error al cambiar el estado de la factura");			
			onResetInvoices();
		} finally {
			setLoadingInvoices((prev) => prev.filter((invoiceId) => invoiceId !== id));
		}
	};

	// Enviar factura para verificación
	const handleSubmit = async (invoice, sendWithIsr) => {
		const { id, items } = invoice;

		if (!areItemsComplete(items, position) || !isInvoiceComplete(invoice, position)) {			
			message.error("No se llenaron todos los rubros necesarios");
			return;
		}
		
		setLoadingInvoices((prev) => [...prev, id]);

		try {
			onChargeNext();
			const invoiceToSend = getInvoiceForSubmission(invoice, company, sendWithIsr, position);
			const res = await edit_invoice(token, "verify", invoiceToSend, position);

			if (res) {
				message.success(res.data);
				onSubmitInvoiceChanges(id, true);
			}
		} catch (error) {
			message.error(error.response?.data || "Error al enviar la factura");
			onResetInvoices();
		} finally {
			setLoadingInvoices((prev) => prev.filter((invoiceId) => invoiceId !== id));
		}
	};

	// Cancela la factura
	const cancelInvoice = async (invoice) => {
		const { id } = invoice;

		const notification = {
			token,
			emition_value: "cancel",
			country: "GT",
			data: {
				id: invoice.id,
				authorization_number: invoice.certification.authorization_number,
				sender_id: company.id,
				receptor_tid: invoice.receptor_id,
				date: invoice.date,
			},
		};

		setLoadingInvoices((prev) => [...prev, id]);
		
		try {
			onChargeNext();
			const res = await emit_invoice(notification);
			if (res) {
				message.success(res.data);
				onSubmitInvoiceChanges(id);
			}
		} catch (error) {
			message.error("Error cancelando factura");
		} finally {
			setLoadingInvoices((prev) => prev.filter((invoiceId) => invoiceId !== id)); // Marca como completada
		}
	};
	
	const handleOpenInvoice = (invoice) => {
		const qrUrl = `https://felpub.c.sat.gob.gt/verificador-web/publico/vistas/verificacionDte.jsf?tipo=autorizacion&numero=${invoice.certification.authorization_number}&emisor=${invoice.sender_id}&receptor=${invoice.receptor_id}`;
		window.open(qrUrl, "_blank");
	};

	const queueHandleInvoiceStateChange = (editValue, invoice, state = null) => {
		enqueueProcessing(handleInvoiceStateChange, [editValue, invoice, state]);
	};
	
	const queueHandleSubmit = (invoice, sendWithIsr) => {
		enqueueProcessing(handleSubmit, [invoice, sendWithIsr]);
	};

	const queueHandleCancel = (invoice) => {
		enqueueProcessing(cancelInvoice, [invoice]);
	};

	return {
		loadingInvoices,
		queueHandleInvoiceStateChange,
		queueHandleSubmit,
		queueHandleCancel,
		handleOpenInvoice,
	};
};
